<script>
import { onMount, onDestroy } from 'svelte';
import { getSchedule } from './services';
import { minuteOnMinuteInterval } from './util';
import SummaryBox from './SummaryBox.svelte';
import EventCard from './EventCard.svelte';
import { updateEventState, Live } from './events';
import { now } from 'svelte/internal';

let loading = true;
let days = []
let filteredDays = []
let filterString = '';
let showInstructions = false;

let refs = [];

const scrollIntoView = ref => {
  const headerHeight = 100;
  window.scrollTo({top: ref.offsetTop - headerHeight, behavior: 'smooth'});
}

const dateToHeaderString = date => date.toLocaleString('default', { weekday: 'long', month: 'long', day: 'numeric' });
const dateToWeekday = date => date.toLocaleString('default', { weekday: 'long' })

onMount(async () => {
  // Get and update event state given current time
  const events = updateEventState(await getSchedule());

  // Group sessions by day
  const daysGroup = events.reduce((days, event) => {
    const date = event.startTime.toLocaleDateString();
    if (!days[date]) days[date] = {events: [], date: event.startTime};
    days[date].events.push(event);
    return days;
  }, {});
  
  // Move back to array
  const daysArray = Object.keys(daysGroup).map(date => daysGroup[date]);

  loading = false;
  days = daysArray;
  filteredDays = daysArray;

  // Update event state every minute
  const timer = minuteOnMinuteInterval().subscribe(() => {
    days = days.map(day => ({...day, events: updateEventState(day.events)}))
    setFilter(filterString) // Refilter
  });

  return () => timer.unsubscribe();
})

function setFilter(query) {
  const lq = query.toUpperCase().trim();
  if (lq.length === 0) {
    filteredDays = days
    return;
  }

  console.log(days)

  filteredDays = days
    .map(day => ({
      ...day,
      events: day.events.filter(event => event.title.toUpperCase().indexOf(lq) !== -1 || event.description.toUpperCase().indexOf(lq) !== -1)
    }))
    .filter(day => day.events.length > 0)
}

$: setFilter(filterString)
</script>

<div class="section-header">EVENT SCHEDULE</div>

<SummaryBox title="EVENT SCHEDULE" description="Welcome to your personalized Event Schedule.  This schedule gives you an overview of all your available sessions.  When a session becomes live, a “Join” button will appear.  Simply click this button to automatically join the session.  You may scroll or select a future date to see upcoming sessions.  To view sessions that have already aired, check out the On-Demand area.  Make sure to check back here at the end of each session to see what’s next! Session times are displayed based on your local timezone. Please double check your computer's clock settings to ensure you don't miss any sessions." />

<div class="row tocol">
  <div class="col nav-block">
    <div class="filter-box row center mb-16">
      <span class="material-icons">search</span>
      <input type="text" placeholder="Filter" bind:value={filterString} />
    </div>

    {#each days as day, i}
      <div class="nav-item" on:click={() => scrollIntoView(refs[i])}>
        <div>{dateToWeekday(day.date)}</div>
        <!-- <div>{day.date.getDate()}</div> -->
      </div>
    {/each}
  </div>

  <div class="col f1">
    <div class="howtojoin" on:click={() => showInstructions = true}>How do I join a session?</div>
    {#each filteredDays as day, i}
      <div bind:this={refs[i]} class="day-header">{dateToHeaderString(day.date)}</div>
        
      {#each day.events as event}
        <EventCard {...event} />
      {/each}
    {/each}
  </div>
</div>

{#if showInstructions}
  <div class="modal" on:click={() => showInstructions = false}>
    <div class="modal-container col">
      <div class="title">How to join a session</div>
      <p>
        Ten minutes before a session starts, a “Join” button will appear below the start time. Simply click this button to automatically join the session.
      </p>

      <div class="example-card">
        <EventCard state={Live} title="Fly Like A Dragon" description="Do you want to learn to fly? I don't mean the a metaphorical sense... we're not talking about your career here. We're talking about actual flying... like a DRAGON! Now it's your chance to learn how to fly high like the pros!"
          startTime={new Date()}
          endTime={new Date(Date.now() + 60*60*1000)}
          speakers={[]} />
      </div>
      
      <div class="button" style="align-self: center">OK</div>
    </div>
  </div>
{/if}

<style lang="scss">
@import './util';
@import './theme';

.example-card {
  opacity: .4;
  pointer-events: none;
  margin-top: 32px;
}

.day-header {
  font-family: $secondary-font;
  font-size: 28px;
  font-weight: 400;
  margin: 16px 0;
  color: white;

  @include landscape {
    margin-top: 24px;
  }
}

.title {
  margin-bottom: .5rem;
  font-weight: 700;
  font-size: 32px;
  text-transform: uppercase;
}

@include landscape {
  .tocol {
    flex-direction: column;
  }
}

.modal-container {
  background-color: white;
  border-radius: 5px;
  color: black;
  padding: 16px;
  min-width: 320px;
  min-height: 185px;
  max-width: 780px;
}

.day-header:first-child {
  margin-top: 48px;

  @include landscape {
    margin-top: unset;
  }
}

.howtojoin {
  z-index: 1;
  text-decoration: underline;
  cursor: pointer;
  color: white;
  align-self: flex-end;
  margin-bottom: -40px;
  font-family: $secondary-font;

  @include landscape {
    margin-bottom: unset;
    align-self: center;
    margin: 16px 0;
  }
}

.filter-box {
  position: relative;

  > :first-child {
    position: absolute;
    pointer-events: none;
    z-index: 1;
    top: 8px;
    left: 8px;
    color: black;
  }
   input {
    font-size: 16px;
    font-weight: 300;
    padding-left: 34px;
    flex: 1;
    width: 100%;  // safari bug
  }
}


.nav-block {
  color: $schedule-foreground-color;
  font-weight: normal;
  font-size: 20px;
  margin-right: 32px;
}

.nav-item {
  padding: 8px 16px;
  display: flex;
  cursor: pointer;
  color: $schedule-foreground-color;
  // letter-spacing: 0.05em;
  // border-radius: 5px;

  &:hover {
    background-color: $primary-color;
    color: $primary-color-text;
  }

  > :first-child {
    width: 140px;
    // font-weight: bold;
    // letter-spacing: 0.05em;
  }

  > :last-child {
    flex: 1;
    font-weight: bold;
  }
}
</style>